import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { MapPin, CalendarBlank, CurrencyDollar, Car, Package, ShieldCheck, CaretRight, CaretLeft, Check, Plus, Trash, MagnifyingGlass, CreditCard } from "@phosphor-icons/react";

const STEPS = [
  { id: "trip", label: "Viaje", icon: MapPin },
  { id: "budget", label: "Presupuesto", icon: CurrencyDollar },
  { id: "payment", label: "Pago", icon: CreditCard },
  { id: "vehicle", label: "Vehiculo", icon: Car },
  { id: "inventory", label: "Inventario", icon: Package },
  { id: "safety", label: "Seguridad", icon: ShieldCheck },
];

const CATEGORY_LABELS = {
  alimentos: "Alimentos", hospedaje: "Hospedaje", casetas: "Casetas",
  autobus: "Autobus", vuelos: "Vuelos", gasolina: "Gasolina",
  incidentes: "Incidentes", otros: "Otros",
};

const SAFETY_ITEMS = [
  { key: "chaleco", label: "Chaleco" },
  { key: "zapato_industrial", label: "Zapato Industrial" },
  { key: "casco", label: "Casco" },
  { key: "tapon_auditivo", label: "Tapon Auditivo" },
  { key: "lentes", label: "Lentes de Seguridad" },
];

export function ViaticosRequestForm({ onSuccess }) {
  const { token } = useAuth();
  const [step, setStep] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [comercialContacts, setComercialContacts] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [clientSearch, setClientSearch] = useState("");

  const [form, setForm] = useState({
    visit_type: "",
    clients: [],
    location: "",
    date_start: "",
    date_end: "",
    budget_items: [],
    description: "",
    vehicle_id: "",
    vehicle_plate: "",
    vehicle_name: "",
    odometer_start: "",
    inventory_items: [],
    safety_social_security: false,
    safety_equipment: {},
    payment_method: "",
    payment_transfer_id: "",
    payment_transfer_extra: "",
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, cRes, catRes] = await Promise.all([
          axios.get(`${API}/viaticos/vehicles`, { headers }),
          axios.get(`${API}/comercial`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/viaticos/catalogs`, { headers }),
        ]);
        setVehicles(vRes.data.filter(v => v.active !== false));
        setComercialContacts(Array.isArray(cRes.data) ? cRes.data : []);
        setVisitTypes(catRes.data.visit_types || []);
      } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
    };
    load();
  }, [token]);

  const updateForm = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const selectVehicle = (v) => {
    updateForm("vehicle_id", v.id);
    updateForm("vehicle_plate", v.plate);
    updateForm("vehicle_name", `${v.brand} ${v.model} - ${v.plate}`);
  };

  const addInventoryItem = () => {
    setForm(p => ({
      ...p,
      inventory_items: [...p.inventory_items, { description: "", serial_number: "", conditions: "" }],
    }));
  };

  const updateInventoryItem = (idx, field, value) => {
    setForm(p => {
      const items = [...p.inventory_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...p, inventory_items: items };
    });
  };

  const removeInventoryItem = (idx) => {
    setForm(p => ({ ...p, inventory_items: p.inventory_items.filter((_, i) => i !== idx) }));
  };

  const toggleSafety = (key) => {
    setForm(p => ({
      ...p,
      safety_equipment: { ...p.safety_equipment, [key]: !p.safety_equipment[key] },
    }));
  };

  const toggleClient = (name) => {
    setForm(p => {
      const exists = p.clients.includes(name);
      return { ...p, clients: exists ? p.clients.filter(c => c !== name) : [...p.clients, name] };
    });
  };

  // Budget category handlers
  const toggleBudgetCategory = (category) => {
    setForm(p => {
      const exists = p.budget_items.find(b => b.category === category);
      if (exists) {
        return { ...p, budget_items: p.budget_items.filter(b => b.category !== category) };
      }
      return { ...p, budget_items: [...p.budget_items, { category, amount: "" }] };
    });
  };

  const updateBudgetAmount = (category, amount) => {
    setForm(p => ({
      ...p,
      budget_items: p.budget_items.map(b => b.category === category ? { ...b, amount } : b),
    }));
  };

  const totalBudget = form.budget_items.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);

  // Calculate number of days
  const numDays = (() => {
    try {
      if (form.date_start && form.date_end) {
        const d1 = new Date(form.date_start);
        const d2 = new Date(form.date_end);
        return Math.max(1, Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24)) + 1);
      }
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
    return 1;
  })();

  const totalWithDays = totalBudget * numDays;

  const handleSubmit = async () => {
    if (!form.visit_type) { toast.error("Selecciona el tipo de visita"); return; }
    if (!form.location) { toast.error("Indica el lugar de visita"); return; }
    if (!form.date_start || !form.date_end) { toast.error("Indica las fechas del viaje"); return; }
    if (form.budget_items.length === 0 || totalBudget <= 0) { toast.error("Agrega al menos una categoria con monto al presupuesto"); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/viaticos/solicitudes`, {
        ...form,
        budget_items: form.budget_items.map(b => ({ ...b, amount: parseFloat(b.amount) || 0 })),
      }, { headers });
      toast.success("Solicitud creada exitosamente");
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear solicitud");
    }
    setSubmitting(false);
  };

  // Filter contacts by search
  const filteredContacts = comercialContacts.filter(c => {
    if (!clientSearch) return true;
    const q = clientSearch.toLowerCase();
    const name = (c.company_name || c.contact_name || c.name || "").toLowerCase();
    return name.includes(q);
  });

  return (
    <div className="max-w-3xl" data-testid="viaticos-request-form">
      {/* Steps indicator */}
      <div className="flex items-center gap-1 mb-6 bg-slate-100 p-1 rounded-lg w-fit overflow-x-auto">
        {STEPS.map((s, i) => (
          <button key={s.id} onClick={() => setStep(i)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-1.5 transition-colors whitespace-nowrap ${step === i ? "bg-white text-slate-900 shadow-sm" : step > i ? "text-emerald-600" : "text-slate-400"}`}
            data-testid={`viaticos-step-${s.id}`}>
            {step > i ? <Check size={14} weight="bold" className="text-emerald-500" /> : <s.icon size={14} />}
            {s.label}
          </button>
        ))}
      </div>

      {/* STEP 1: Trip Info */}
      {step === 0 && (
        <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="viaticos-step-trip-form">
          <h3 className="font-semibold text-slate-800">Informacion del Viaje</h3>

          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Tipo de Visita *</label>
            <div className="flex flex-wrap gap-2">
              {visitTypes.map(vt => (
                <button key={vt} onClick={() => updateForm("visit_type", vt)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${form.visit_type === vt ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}
                  data-testid={`viaticos-visit-${vt}`}>
                  {vt}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Clientes a Visitar</label>
            {/* Search */}
            <div className="relative mb-2">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                placeholder="Buscar cliente por nombre..." className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="viaticos-client-search" />
            </div>
            {filteredContacts.length > 0 ? (
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 bg-slate-50 rounded-lg border">
                {filteredContacts.map(c => {
                  const name = c.company_name || c.contact_name || c.name || "";
                  return (
                    <button key={c.id} onClick={() => toggleClient(name)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.clients.includes(name) ? "bg-blue-100 text-blue-800 border-blue-300" : "bg-white text-slate-600 border-slate-200 hover:border-blue-200"}`}
                      data-testid={`viaticos-client-${c.id}`}>
                      {name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-400 py-2">{clientSearch ? "Sin resultados para la busqueda" : "No hay contactos comerciales registrados."}</p>
            )}
            {form.clients.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {form.clients.map((c, i) => (
                  <span key={`client-${c}`} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full flex items-center gap-1">
                    {c}
                    <button onClick={() => toggleClient(c)} className="text-blue-400 hover:text-red-500">&times;</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500">Lugar *</label>
            <input value={form.location} onChange={e => updateForm("location", e.target.value)}
              placeholder="Ej: Monterrey, NL" className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="viaticos-location" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500">Inicio del Viaje *</label>
              <input type="date" value={form.date_start} onChange={e => updateForm("date_start", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="viaticos-date-start" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Fin del Viaje *</label>
              <input type="date" value={form.date_end} onChange={e => updateForm("date_end", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="viaticos-date-end" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500">Descripcion / Notas</label>
            <textarea value={form.description} onChange={e => updateForm("description", e.target.value)}
              rows={3} placeholder="Detalle del viaje..." className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="viaticos-description" />
          </div>
        </div>
      )}

      {/* STEP 2: Budget by category */}
      {step === 1 && (
        <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="viaticos-step-budget-form">
          <h3 className="font-semibold text-slate-800">Presupuesto por Categoria</h3>
          <p className="text-xs text-slate-500">
            Selecciona las categorias e ingresa el <strong>monto por dia</strong>.
            {numDays > 1 && <span className="text-blue-600 font-medium"> Se multiplicara por {numDays} dias del viaje.</span>}
          </p>

          <div className="grid grid-cols-2 gap-3">
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
              const selected = form.budget_items.find(b => b.category === key);
              const perDay = parseFloat(selected?.amount) || 0;
              return (
                <div key={key} className={`rounded-lg border p-3 transition-colors ${selected ? "bg-blue-50 border-blue-300" : "bg-white border-slate-200"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <button onClick={() => toggleBudgetCategory(key)}
                      className="flex items-center gap-2 text-sm font-medium text-slate-700"
                      data-testid={`budget-toggle-${key}`}>
                      {selected
                        ? <Check size={16} weight="bold" className="text-blue-600" />
                        : <div className="w-4 h-4 rounded border border-slate-300" />
                      }
                      {label}
                    </button>
                  </div>
                  {selected && (
                    <div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                        <input type="number" value={selected.amount}
                          onChange={e => updateBudgetAmount(key, e.target.value)}
                          placeholder="Monto por dia"
                          className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm bg-white"
                          data-testid={`budget-amount-${key}`} />
                      </div>
                      {numDays > 1 && perDay > 0 && (
                        <p className="text-[11px] text-blue-600 mt-1">${perDay.toLocaleString()} x {numDays} dias = <strong>${(perDay * numDays).toLocaleString()}</strong></p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {form.budget_items.length > 0 && (
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">Subtotal por dia:</span>
                <span className="text-sm text-slate-700">${totalBudget.toLocaleString()}</span>
              </div>
              {numDays > 1 && (
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm font-medium text-slate-600">x {numDays} dias</span>
                </div>
              )}
              <div className="flex items-center justify-between mt-1 pt-1 border-t">
                <span className="text-sm font-bold text-slate-800">Total Presupuesto:</span>
                <span className="text-xl font-bold text-slate-900" data-testid="budget-total">${totalWithDays.toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* STEP 3: Payment Method */}
      {step === 2 && (
        <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="viaticos-step-payment-form">
          <h3 className="font-semibold text-slate-800">Metodo de Pago</h3>
          <p className="text-xs text-slate-500">Selecciona como se entregara el recurso del viatico.</p>
          <div className="grid grid-cols-1 gap-2">
            {[
              { value: "efectivo", label: "Efectivo", desc: "Entrega en efectivo" },
              { value: "transferencia", label: "Transferencia", desc: "Deposito bancario a cuenta de la empresa" },
              { value: "cheque", label: "Cheque", desc: "Pago mediante cheque" },
              { value: "tarjeta_credito", label: "Tarjeta de Credito", desc: "Cargo a tarjeta corporativa" },
              { value: "reembolso", label: "Reembolso al Empleado", desc: "El empleado paga y se le reembolsa despues" },
            ].map(pm => (
              <button key={pm.value} onClick={() => updateForm("payment_method", pm.value)}
                className={`p-3 rounded-lg border text-left transition-colors ${form.payment_method === pm.value ? "bg-blue-50 border-blue-400 ring-1 ring-blue-400" : "bg-white border-slate-200 hover:border-blue-300"}`}
                data-testid={`payment-${pm.value}`}>
                <p className="font-medium text-sm text-slate-800">{pm.label}</p>
                <p className="text-xs text-slate-500">{pm.desc}</p>
              </button>
            ))}
          </div>

          {form.payment_method === "transferencia" && (
            <div className="space-y-3 bg-slate-50 rounded-lg p-4">
              <div>
                <label className="text-xs font-medium text-slate-500">ID de Transferencia de la Empresa *</label>
                <input value={form.payment_transfer_id} onChange={e => updateForm("payment_transfer_id", e.target.value)}
                  placeholder="Ej: TRF-2026-001" className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5 bg-white" data-testid="payment-transfer-id" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Referencia Extra</label>
                <input value={form.payment_transfer_extra} onChange={e => updateForm("payment_transfer_extra", e.target.value)}
                  placeholder="Banco, cuenta, referencia adicional" className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5 bg-white" data-testid="payment-transfer-extra" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* STEP 4: Vehicle */}
      {step === 3 && (
        <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="viaticos-step-vehicle-form">
          <h3 className="font-semibold text-slate-800">Vehiculo</h3>
          {vehicles.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {vehicles.map(v => (
                <button key={v.id} onClick={() => selectVehicle(v)}
                  className={`p-3 rounded-lg border text-left transition-colors ${form.vehicle_id === v.id ? "bg-blue-50 border-blue-400 ring-1 ring-blue-400" : "bg-white border-slate-200 hover:border-blue-300"}`}
                  data-testid={`viaticos-vehicle-${v.id}`}>
                  <p className="font-bold text-sm text-slate-800">{v.plate}</p>
                  <p className="text-xs text-slate-500">{v.brand} {v.model} {v.year}</p>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
              No hay vehiculos registrados. Pide al administrador que agregue vehiculos desde el catalogo.
            </div>
          )}

          {form.vehicle_id && (
            <div>
              <label className="text-xs font-medium text-slate-500">Odometro de Salida (km)</label>
              <input type="number" value={form.odometer_start} onChange={e => updateForm("odometer_start", e.target.value)}
                placeholder="Ej: 45000" className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="viaticos-odometer" />
            </div>
          )}
        </div>
      )}

      {/* STEP 5: Inventory */}
      {step === 4 && (
        <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="viaticos-step-inventory-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Inventario / Material para Instalacion</h3>
            <button onClick={addInventoryItem} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 flex items-center gap-1" data-testid="viaticos-add-inventory">
              <Plus size={14} /> Agregar
            </button>
          </div>

          {form.inventory_items.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Sin material registrado. Haz clic en "Agregar" si llevas equipo.</p>
          ) : (
            <div className="space-y-3">
              {form.inventory_items.map((item, idx) => (
                <div key={item.serial_number || `inv-${idx}`} className="bg-slate-50 rounded-lg p-3 space-y-2 relative" data-testid={`viaticos-inventory-${idx}`}>
                  <button onClick={() => removeInventoryItem(idx)} className="absolute top-2 right-2 text-red-400 hover:text-red-600">
                    <Trash size={16} />
                  </button>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[11px] text-slate-500">Descripcion</label>
                      <input value={item.description} onChange={e => updateInventoryItem(idx, "description", e.target.value)}
                        className="w-full px-2 py-1.5 border rounded text-sm" placeholder="Ej: Router Cisco" />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-500">No. Serie</label>
                      <input value={item.serial_number} onChange={e => updateInventoryItem(idx, "serial_number", e.target.value)}
                        className="w-full px-2 py-1.5 border rounded text-sm" placeholder="Ej: SN-12345" />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-500">Condiciones</label>
                      <input value={item.conditions} onChange={e => updateInventoryItem(idx, "conditions", e.target.value)}
                        className="w-full px-2 py-1.5 border rounded text-sm" placeholder="Ej: Nuevo / Usado" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STEP 6: Safety */}
      {step === 5 && (
        <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="viaticos-step-safety-form">
          <h3 className="font-semibold text-slate-800">Seguridad</h3>

          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={form.safety_social_security} onChange={e => updateForm("safety_social_security", e.target.checked)}
                className="sr-only peer" data-testid="viaticos-social-security" />
              <div className="w-9 h-5 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
            </label>
            <span className="text-sm font-medium text-slate-700">Hoja de Seguridad Social</span>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 mb-2 block">Equipo de Seguridad</label>
            <div className="grid grid-cols-2 gap-2">
              {SAFETY_ITEMS.map(item => (
                <button key={item.key} onClick={() => toggleSafety(item.key)}
                  className={`flex items-center gap-2 p-3 rounded-lg border text-sm font-medium transition-colors ${form.safety_equipment[item.key] ? "bg-emerald-50 border-emerald-400 text-emerald-800" : "bg-white border-slate-200 text-slate-600 hover:border-emerald-300"}`}
                  data-testid={`viaticos-safety-${item.key}`}>
                  {form.safety_equipment[item.key] ? <Check size={16} weight="bold" className="text-emerald-600" /> : <div className="w-4 h-4 rounded border border-slate-300" />}
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
          className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-1 disabled:opacity-40" data-testid="viaticos-prev-step">
          <CaretLeft size={16} /> Anterior
        </button>

        {step < STEPS.length - 1 ? (
          <button onClick={() => setStep(step + 1)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1" data-testid="viaticos-next-step">
            Siguiente <CaretRight size={16} />
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={submitting} className="px-6 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-1.5 disabled:opacity-50" data-testid="viaticos-submit">
            <Check size={16} weight="bold" /> {submitting ? "Enviando..." : "Enviar Solicitud"}
          </button>
        )}
      </div>
    </div>
  );
}
